import type { GetDocumentsQuery } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Input,
  Popover,
  Portal,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#src/components/ui/toaster';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { GET_DOCUMENTS } from '#src/graphql/queries/documents';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useAppSettings } from '#src/providers/app-settings-provider';
import {
  usePageFooter,
  usePageLayout,
} from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import {
  isValidDocumentPath,
  DOCUMENT_PATH_DESCRIPTION,
} from '#src/utils/document-path';
import { formatDocumentName } from '#src/utils/format-document-name';

export function Dashboard() {
  const navigate = useNavigate();
  const { footerRef } = usePageFooter();
  const { setFooterMode } = usePageLayout();
  const user = useCurrentUser();
  const { documentPathRegex } = useAppSettings();

  useEffect(() => {
    setFooterMode('fixed');
    return () => setFooterMode('float');
  }, [setFooterMode]);
  const { data } = useQuery(GET_DOCUMENTS);
  const documents = data?.documents || [];
  const [createPath, setCreatePath] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [messageText, setMessageText] = useState('');

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);

  const {
    data: messagesData,
    loading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    fetchPolicy: 'cache-and-network',
  });

  const filteredMessages = [...(messagesData?.messages || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredMessages]);

  const handleDocumentSelect = (
    document: GetDocumentsQuery['documents'][number],
  ) => {
    navigate(`/documents/${document.path}`);
  };

  const trimmedCreatePath = createPath.trim();
  const isCreatePathValid = trimmedCreatePath
    ? isValidDocumentPath(trimmedCreatePath, documentPathRegex)
    : false;

  const handleCreateSubmit = () => {
    const path = createPath.trim();
    if (!path || !isValidDocumentPath(path, documentPathRegex)) return;
    setCreatePath('');
    setIsPopoverOpen(false);
    navigate(`/documents/${path}`);
  };

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: { input: { value } },
      });
      setMessageText('');
      await refetchMessages();
      toaster.create({
        title: 'Message sent',
        description: 'Your message was created successfully',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const createDocumentPopover = (
    <Popover.Root
      open={isPopoverOpen}
      onOpenChange={(e) => setIsPopoverOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <Button size="sm" colorScheme="blue">
          Create
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                Create Document
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Enter a path to create a new document. You'll be taken to the
                document page where you can create it.
              </Text>
              <Input
                placeholder="e.g. folder/doc-name or path (with parens)"
                value={createPath}
                onChange={(e) => setCreatePath(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateSubmit();
                  if (e.key === 'Escape') {
                    setIsPopoverOpen(false);
                    setCreatePath('');
                  }
                }}
                size="sm"
                autoFocus
                aria-invalid={!!trimmedCreatePath && !isCreatePathValid}
                borderColor={
                  trimmedCreatePath && !isCreatePathValid
                    ? 'red.500'
                    : undefined
                }
              />
              {trimmedCreatePath && !isCreatePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
              <HStack gap={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsPopoverOpen(false);
                    setCreatePath('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleCreateSubmit}
                  disabled={!trimmedCreatePath || !isCreatePathValid}
                >
                  Create
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );

  const footerContent = user.email ? (
    <HStack gap={2} align="flex-end" width="100%">
      <Textarea
        flex={1}
        placeholder="Type a message..."
        value={messageText}
        onChange={(e) => setMessageText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
          }
        }}
        size="sm"
        fontSize="16px"
        rows={2}
        resize="none"
        minH="40px"
        maxH="120px"
      />
      <Button
        size="sm"
        colorScheme="blue"
        onClick={handleSendMessage}
        disabled={!messageText.trim() || isSendingMessage}
        loading={isSendingMessage}
        loadingText="Sending..."
      >
        Send
      </Button>
    </HStack>
  ) : null;

  return (
    <>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        minHeight={0}
        gap={4}
      >
        <HStack
          flex={1}
          minHeight={0}
          align="stretch"
          gap={4}
          overflow="hidden"
        >
          {/* Table of Contents — 1/4 width, independently scrollable; hidden on small screens */}
          <Box
            width="25%"
            flexShrink={0}
            minHeight={0}
            bg="bg"
            shadow="lg"
            borderRadius="lg"
            display={{ base: 'none', md: 'flex' }}
            flexDirection="column"
            overflow="hidden"
          >
            <HStack
              justify="space-between"
              align="center"
              p={4}
              pb={2}
              flexShrink={0}
            >
              <Text fontWeight="semibold" fontSize="sm" color="fg">
                Table of Contents
              </Text>
              {createDocumentPopover}
            </HStack>
            <Box flex={1} minHeight={0} overflowY="auto" px={4} pb={4}>
              {documents.length === 0 ? (
                <VStack gap={3} align="stretch" pt={2}>
                  <Text fontSize="sm" color="fg.muted" textAlign="center">
                    No documents yet. Use Create above to get started.
                  </Text>
                </VStack>
              ) : (
                <VStack gap={1} align="stretch">
                  {documents.map(
                    (document: GetDocumentsQuery['documents'][number]) => (
                      <Box
                        key={document.id}
                        p={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg="transparent"
                        _hover={{ bg: 'bg.subtle' }}
                        onClick={() => handleDocumentSelect(document)}
                      >
                        <HStack justify="space-between" align="center">
                          <Text
                            fontWeight="medium"
                            fontSize="sm"
                            color="fg"
                            _hover={{ color: 'blue.600' }}
                          >
                            {formatDocumentName(document.path).join(' > ')}
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            {new Date(document.updatedAt).toLocaleDateString()}
                          </Text>
                        </HStack>
                      </Box>
                    ),
                  )}
                </VStack>
              )}
            </Box>
          </Box>

          {/* Recent Messages — 3/4 width, scrollable, newest at bottom */}
          <Box
            flex={1}
            minWidth={0}
            minHeight={0}
            bg="bg"
            shadow="lg"
            borderRadius="lg"
            p={6}
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <Heading size="md" color="fg" flexShrink={0} mb={4}>
              Recent Messages
            </Heading>

            <Box flex={1} minHeight={0} overflowY="auto">
              {!user.email ? (
                <Alert.Root status="info">
                  <Alert.Indicator />
                  <Alert.Title>
                    Please set your user email in Settings to view messages.
                  </Alert.Title>
                </Alert.Root>
              ) : isLoadingMessages ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                  <Text fontSize="sm" color="fg.muted" mt={4}>
                    Loading messages...
                  </Text>
                </Box>
              ) : messagesError ? (
                <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>
                    Error loading messages: {messagesError.message}
                  </Alert.Title>
                </Alert.Root>
              ) : filteredMessages.length === 0 ? (
                <Text color="fg.muted" textAlign="center" py={8}>
                  No messages found.
                </Text>
              ) : (
                <VStack gap={3} align="stretch">
                  {filteredMessages.map((message) => (
                    <Box
                      key={message.id}
                      p={4}
                      bg="bg.subtle"
                      borderRadius="md"
                      border="1px"
                      borderColor="border"
                    >
                      <HStack justify="space-between" align="start" mb={2}>
                        <Text fontSize="xs" color="fg.muted">
                          {new Date(message.createdAt).toLocaleString()}
                        </Text>
                        <HStack gap={2} align="center">
                          <Text fontSize="xs" color="fg.subtle">
                            {message.source}
                          </Text>
                        </HStack>
                      </HStack>
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {message.value}
                      </Text>
                      <HStack gap={2} align="center" mt={2} wrap="wrap">
                        <HStack gap={1} align="center">
                          <Text fontSize="xs" color="fg.subtle">
                            ID: {message.id}
                          </Text>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            color="fg.subtle"
                            _hover={{ color: 'fg.muted' }}
                            onClick={() => {
                              navigator.clipboard.writeText(message.id);
                            }}
                          >
                            <FaCopy />
                          </IconButton>
                        </HStack>
                        {message.messageContexts &&
                          message.messageContexts.length > 0 && (
                            <HStack gap={1} align="center" wrap="wrap">
                              {message.messageContexts.map((context, index) => (
                                <Badge
                                  key={`${context.name}-${index}`}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="subtle"
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                >
                                  {context.name}: {context.value}
                                </Badge>
                              ))}
                            </HStack>
                          )}
                      </HStack>
                    </Box>
                  ))}
                  <Box ref={messagesEndRef} />
                </VStack>
              )}
            </Box>
          </Box>
        </HStack>
      </Box>
    </>
  );
}
